import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Plus, Megaphone, Trash2, Pencil, Loader2, X, Info, AlertTriangle, CheckCircle2 } from "lucide-react";

const SEV = [
  { id: "info", label: "Info", tone: "bg-sky-50 text-sky-700", icon: Info },
  { id: "success", label: "Success", tone: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  { id: "warning", label: "Warning", tone: "bg-amber-50 text-amber-700", icon: AlertTriangle },
];
const AUDIENCE = ["all", "admin", "teacher", "student"];

const sevMeta = (id) => SEV.find((s) => s.id === id) || SEV[0];

const EMPTY = { title: "", body: "", severity: "info", audience: "all", active: true };

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); setErr(""); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, body: a.body, severity: a.severity, audience: a.audience, active: a.active });
    setErr("");
  };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (editing === "new") await api.post("/announcements", form);
      else await api.patch(`/announcements/${editing.id}`, form);
      close(); load();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (a) => {
    await api.patch(`/announcements/${a.id}`, { active: !a.active });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/announcements/${id}`);
    load();
  };

  return (
    <AppShell title="Announcements" subtitle="Broadcast messages that appear on dashboards.">
      <div className="flex justify-end mb-4">
        <button onClick={openNew} className="btn-primary text-sm" data-testid="new-announcement-btn">
          <Plus className="w-4 h-4" /> New announcement
        </button>
      </div>

      <div className="space-y-3" data-testid="announcement-list">
        {items.map((a) => {
          const meta = sevMeta(a.severity);
          const Icon = meta.icon;
          return (
            <div key={a.id} className="qs-card p-5" data-testid={`announcement-card-${a.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-xl grid place-items-center ${meta.tone}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-lg font-bold text-slate-900">{a.title}</h3>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.tone}`}>{a.severity}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                        {a.audience}
                      </span>
                      {!a.active && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">
                          inactive
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{a.body}</p>
                    <p className="text-[10px] text-slate-400 mt-2">
                      By {a.created_by_name || "admin"} · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
                  <button
                    onClick={() => toggleActive(a)}
                    className={`text-xs font-semibold px-3 py-1 rounded-full ${a.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                    data-testid={`announcement-toggle-${a.id}`}
                  >
                    {a.active ? "Active" : "Paused"}
                  </button>
                  <button onClick={() => openEdit(a)} className="text-slate-500 hover:text-indigo-600" data-testid={`announcement-edit-${a.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(a.id)} className="text-rose-500" data-testid={`announcement-delete-${a.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-center text-slate-500 py-12" data-testid="no-announcements">
            <Megaphone className="w-6 h-6 mx-auto mb-2 opacity-50" /> No announcements yet.
          </p>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="announcement-modal">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-slate-900">
                {editing === "new" ? "New announcement" : "Edit announcement"}
              </h3>
              <button type="button" onClick={close} className="text-slate-500 hover:text-slate-800" data-testid="announcement-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Title</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="announcement-title-input"
            />
            <label className="block mt-3 text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Body</label>
            <textarea
              required
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={4}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="announcement-body-input"
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Severity</label>
                <select
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white outline-none"
                  data-testid="announcement-severity-select"
                >
                  {SEV.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Audience</label>
                <select
                  value={form.audience}
                  onChange={(e) => setForm({ ...form, audience: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white outline-none"
                  data-testid="announcement-audience-select"
                >
                  {AUDIENCE.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700 select-none">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                data-testid="announcement-active-toggle"
              /> Active immediately
            </label>
            {err && <p className="mt-3 text-sm text-rose-600" data-testid="announcement-error">{err}</p>}
            <button disabled={busy} className="btn-primary text-sm mt-5 w-full justify-center" data-testid="announcement-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {editing === "new" ? "Publish" : "Save"}
            </button>
          </form>
        </div>
      )}
    </AppShell>
  );
}
